// Das Gedächtnis (docs/gedaechtnis.md): der Stand des Index (Zahlen, keine
// Inhalte) für Baum und Seite. GET /v2-api/gedaechtnis ist Boot-Hydration
// und Abgleich; jede Änderung kommt über den WS-Kanal "gedaechtnis".
// Suchen und Lesen geben Inhalte heraus und stehen hinter der PIN -- nur
// auf der Seite (GedaechtnisSuche).

import { SvelteMap } from "svelte/reactivity";

import { postJSON } from "./api";
import { pinNoetig } from "./claude/chat.svelte";
import type { Ergebnis, GedaechtnisStand, Lesen, Zugriff } from "./gedaechtnis/gedaechtnis";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import { registerRevalidator } from "./revalidate.svelte";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

const STORAGE_KEY = "gedaechtnis";
const ABGLEICH_MS = 120_000;

const _s = new SvelteMap<string, GedaechtnisStand>();
let _started = false;
let _startLauf = 0;
let _timer: ReturnType<typeof setInterval> | undefined;
let _unsubscribe: (() => void) | undefined;

function uebernimm(s: GedaechtnisStand): void {
  if (!s || typeof s.eintraege !== "object") return;
  _s.set("current", s);
  persist(STORAGE_KEY, s);
}

async function refresh(): Promise<void> {
  try {
    const r = await fetch("/v2-api/gedaechtnis", { credentials: "include" });
    if (!r.ok) return;
    uebernimm((await r.json()) as GedaechtnisStand);
  } catch {
    // Dienst startet neu -- der nächste Abgleich holt es nach.
  }
}

export function startGedaechtnis(): void {
  if (_started) return;
  _started = true;
  const lauf = ++_startLauf;
  // Wie startKi: der gespeicherte Stand kommt erst nach dem Zeichnen in
  // den Store (state_unsafe_mutation, 21.10.2026).
  queueMicrotask(() => {
    if (lauf !== _startLauf) return;
    registerRevalidator(refresh);
    const cached = loadPersisted<GedaechtnisStand>(STORAGE_KEY);
    if (cached?.eintraege) _s.set("current", cached);
    void refresh();
    _timer = setInterval(refresh, ABGLEICH_MS);
    _unsubscribe = wsSubscribe("gedaechtnis", (d) => uebernimm(d as GedaechtnisStand));
  });
}

export function stopGedaechtnis(): void {
  _startLauf++;
  _started = false;
  if (_timer) clearInterval(_timer);
  _timer = undefined;
  _unsubscribe?.();
  _unsubscribe = undefined;
  _s.clear();
}

export function getGedaechtnis(): GedaechtnisStand | undefined {
  return _s.get("current");
}

/** Index verwerfen und neu lesen (die Vektoren bleiben). */
export async function neuEinlesen(): Promise<void> {
  await postJSON("/v2-api/gedaechtnis/neu-aufbauen", {});
}

/** Suche und Lesen einer Seite -- hinter der PIN. Eine neue Frage
 *  verwirft eine noch laufende (nur die jüngste Antwort zählt). */
export class GedaechtnisSuche {
  ergebnis = $state<Ergebnis | null>(null);
  laedt = $state(false);
  gesperrt = $state(false);
  fehler = $state<string | null>(null);
  #lauf = 0;

  async suche(frage: string, arten: string[]): Promise<void> {
    const lauf = ++this.#lauf;
    const f = frage.trim();
    if (!f) {
      this.ergebnis = null;
      this.fehler = null;
      this.laedt = false;
      return;
    }
    if (await pinNoetig()) {
      if (lauf === this.#lauf) this.gesperrt = true;
      return;
    }
    this.gesperrt = false;
    this.laedt = true;
    try {
      const q = new URLSearchParams({ frage: f, anzahl: "25" });
      if (arten.length) q.set("art", arten.join(","));
      const r = await fetch(`/v2-api/gedaechtnis/suche?${q}`, { credentials: "include" });
      if (lauf !== this.#lauf) return;
      if (r.status === 423) {
        this.gesperrt = true;
        return;
      }
      if (!r.ok) {
        const d = (await r.json().catch(() => ({}))) as { detail?: string };
        this.fehler = d.detail ?? `Fehler ${r.status}`;
        return;
      }
      this.ergebnis = (await r.json()) as Ergebnis;
      this.fehler = null;
    } catch (e) {
      if (lauf === this.#lauf) this.fehler = e instanceof Error ? e.message : String(e);
    } finally {
      if (lauf === this.#lauf) this.laedt = false;
    }
  }
}

/** Einen Eintrag ganz lesen (Detail einer Zeile); umfeld = Wechsel
 *  davor und danach. */
export async function leseEintrag(id: number, umfeld = 0): Promise<Lesen | "gesperrt"> {
  const r = await fetch(`/v2-api/gedaechtnis/eintrag/${id}?umfeld=${umfeld}`, { credentials: "include" });
  if (r.status === 423) return "gesperrt";
  if (!r.ok) {
    const d = (await r.json().catch(() => ({}))) as { detail?: string };
    throw new Error(d.detail ?? `Fehler ${r.status}`);
  }
  return (await r.json()) as Lesen;
}

/** Die Zugriffe mit Inhalt (wonach gesucht wurde) -- hinter der PIN. Neu
 *  geladen, wenn der Stand einen neuen Zugriff meldet (WS „gedaechtnis").
 *  Eine Seite sind die neuesten 100; „ältere laden" hängt die nächsten an
 *  (wie im Abfrageprotokoll der DNS-Seite). Wer Älteres nachgeladen hat,
 *  behält es, wenn oben Neues dazukommt. */
export const ZUGRIFFE_SEITE = 100;

export class GedaechtnisZugriffe {
  liste = $state<Zugriff[]>([]);
  gesperrt = $state(false);
  fehler = $state<string | null>(null);
  geladen = $state(false);
  laedt = $state(false);
  /** die letzte Seite war voll: es kann Älteres geben */
  mehr = $state(false);
  #stand = 0;

  async #hole(vor: number): Promise<Zugriff[] | null> {
    const r = await fetch(`/v2-api/gedaechtnis/zugriffe?anzahl=${ZUGRIFFE_SEITE}${vor ? `&vor=${vor}` : ""}`, { credentials: "include" });
    if (r.status === 423) {
      this.gesperrt = true;
      return null;
    }
    if (!r.ok) {
      const d = (await r.json().catch(() => ({}))) as { detail?: string };
      this.fehler = d.detail ?? `Fehler ${r.status}`;
      return null;
    }
    this.gesperrt = false;
    this.fehler = null;
    return (await r.json()) as Zugriff[];
  }

  /** Die neueste Seite; schon nachgeladene ältere bleiben dahinter. */
  async lade(): Promise<void> {
    const nr = ++this.#stand;
    try {
      const seite = await this.#hole(0);
      if (!seite || nr !== this.#stand) return;
      const grenze = seite.length ? seite[seite.length - 1].id : 0;
      const nachgeladen = this.liste.length > ZUGRIFFE_SEITE ? this.liste.filter((z) => z.id < grenze) : [];
      this.liste = [...seite, ...nachgeladen];
      if (!nachgeladen.length) this.mehr = seite.length === ZUGRIFFE_SEITE;
      this.geladen = true;
    } catch (e) {
      if (nr === this.#stand) this.fehler = e instanceof Error ? e.message : String(e);
    }
  }

  /** Die nächsten 100 älteren hinten anhängen. */
  async ladeAeltere(): Promise<void> {
    const letzter = this.liste[this.liste.length - 1];
    if (!letzter || this.laedt) return;
    const nr = ++this.#stand;
    this.laedt = true;
    try {
      const seite = await this.#hole(letzter.id);
      if (!seite || nr !== this.#stand) return;
      this.liste = [...this.liste, ...seite];
      this.mehr = seite.length === ZUGRIFFE_SEITE;
    } catch (e) {
      if (nr === this.#stand) this.fehler = e instanceof Error ? e.message : String(e);
    } finally {
      this.laedt = false;
    }
  }
}
